import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Pagination,
  BottomNavigation,
  BottomNavigationAction,
  Skeleton,
  Typography,
  useTheme,
  Chip
} from "@mui/material";
import { Star, ChatBubbleOutline } from "@mui/icons-material";
import { getText } from "../utils/texts";

// helper to format creation_time: if today, show HH:MM; else DD.MM.YYYY.
const formatCreationTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();

  // check if the message was created today.
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else {
    // manually format DD.MM.YYYY with leading zeros.
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }
};

const InboxPage = ({
    currentMessages,
    currentReplies,
    inboxTab,
    setScreenByEvent,
    handleMessageClick,
    handleReplyClick,
    handlePaginationChange,
    language,
    itemsPerPage,
    messages,
    replies,
    currentPage,
    repliesPage,
    isGroup
  }) => {

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [visibleReplies, setVisibleReplies] = useState([]);
  const listRef = useRef(null);
  const [clickedMessages, setClickedMessages] = useState(new Set());
  const [clickedReplies, setClickedReplies] = useState(new Set());

  // Get top 10 not read messages
  const readMessages = messages.filter(msg => !msg.is_read);
  const topMessages = readMessages.slice(0, 10);
  
  const readReplies = replies.filter(reply => !reply.is_read);
  const topReplies = readReplies.slice(0, 10);

  const handleLocalMessageClick = (msg) => {
    setClickedMessages(prev => new Set(prev).add(msg.id));
    handleMessageClick(msg);
  };

  const handleLocalReplyClick = (reply) => {
    setClickedReplies(prev => new Set(prev).add(reply.id));
    handleReplyClick(reply);
  }

  useEffect(() => {
    setVisibleMessages(messages.slice(0, 20)); // Initial load
    setVisibleReplies(replies.slice(0, 20));
  }, [messages, replies]);

  const handleScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      if (inboxTab === "questions" && visibleMessages.length < messages.length) {
        setVisibleMessages(messages.slice(0, visibleMessages.length + 20));
      } else if (inboxTab === "answers" && visibleReplies.length < replies.length) {
        setVisibleReplies(replies.slice(0, visibleReplies.length + 20));
      }
    }
  };

  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (listElement) {
        listElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [visibleMessages, visibleReplies, inboxTab]);

  // Style based on theme
  const containerStyle = {
    bgcolor: isDarkMode ? '#000000' : '#f5f5f8',
    color: isDarkMode ? '#ffffff' : '#000000',
    height: '100%'
  };

  const bottomNavStyle = {
    backgroundColor: isDarkMode ? '#000000' : '#f5f5f8',
    borderTop: `1px solid ${isDarkMode ? '#333333' : '#e0e0e0'}`,
    color: isDarkMode ? '#5188c1' : '#5188c1'
  };

  const bottomNavLabelStyle = {
    color: isDarkMode ? '#aaaaaa' : '#666666'
  };

  const activeBottomNavLabelStyle = {
    color: isDarkMode ? '#5188c1' : '#5188c1'
  };

  const listItemStyle = {
    backgroundColor: isDarkMode ? '#0e1621' : '#e9f0f8',
    borderRadius: 1,
    mb: 1,
    '&:hover': {
      backgroundColor: isDarkMode ? '#172836' : '#d9e5f0'
    }
  };

  const topMessageHeaderStyle = {
    margin: '20px 0',
    color: isDarkMode ? '#ffffff' : '#000000',
    fontWeight: 'bold'
  };

  return(
    <Box sx={containerStyle}>
      <BottomNavigation
        value={inboxTab}
        onChange={(event, newValue) => setScreenByEvent(newValue)}
        showLabels
        sx={bottomNavStyle}
      >
        <BottomNavigationAction 
          label={getText("questions", language)} 
          value="questions" 
          icon={<Box component="span">→</Box>}
          sx={{
            '& .MuiBottomNavigationAction-label': {
              ...bottomNavLabelStyle,
              ...(inboxTab === "questions" ? activeBottomNavLabelStyle : {})
            },
            '& .MuiSvgIcon-root': {
              color: isDarkMode ? '#5188c1' : '#5188c1'
            }
          }}
        />
        <BottomNavigationAction 
          label={getText("answers", language)} 
          value="answers" 
          icon={<ChatBubbleOutline />}
          sx={{
            '& .MuiBottomNavigationAction-label': {
              ...bottomNavLabelStyle,
              ...(inboxTab === "answers" ? activeBottomNavLabelStyle : {})
            }
          }}
        />
      </BottomNavigation>
      
      <Box ref={listRef} sx={{ height: "calc(100vh - 56px)", overflowY: "auto", p: 2 }}>
        {/* Display top 10 read messages if on questions tab */}
        {inboxTab === "questions" && topMessages.length > 0 && (
          <Box>
            <Typography variant="h6" sx={topMessageHeaderStyle}>
              {getText("topReadMessages", language) || "Top Messages"}
            </Typography>
            <List>
              {topMessages.map((msg) => (
                <ListItem
                  button
                  key={`top-${msg.id}`}
                  onClick={() => handleLocalMessageClick(msg)}
                  sx={listItemStyle}
                >
                  <ListItemText
                    primary={msg.shortText}
                    secondary={formatCreationTime(msg.creation_time)}
                    primaryTypographyProps={{
                      color: isDarkMode ? '#ffffff' : '#000000'
                    }}
                    secondaryTypographyProps={{
                      color: isDarkMode ? '#aaaaaa' : '#666666'
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMessageClick(msg);
                    }}
                    sx={{ 
                      textTransform: "none", 
                      fontSize: "0.8rem",
                      bgcolor: "#5188c1",
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: '#3a6fa5'
                      }
                    }}
                  >
                    {getText("reply", language)}
                  </Button>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        {/* Display top 10 read replies if on answers tab */}
        {inboxTab === "answers" && topReplies.length > 0 && !isGroup && (
          <Box>
            <Typography variant="h6" sx={topMessageHeaderStyle}>
              {getText("topReadReplies", language) || "Top Replies"}
            </Typography>
            <List>
              {topReplies.map((reply) => (
                <ListItem
                  button
                  key={`top-${reply.id}`}
                  onClick={() => handleLocalReplyClick(reply)}
                  sx={listItemStyle}
                >
                  <ListItemText 
                    primary={`${getText("questionDiv", language)} ${reply.message_text}`}
                    secondary={`${getText("replyDiv", language)} ${reply.fullText}`}
                    primaryTypographyProps={{
                      color: isDarkMode ? '#ffffff' : '#000000'
                    }}
                    secondaryTypographyProps={{
                      color: isDarkMode ? '#aaaaaa' : '#666666'
                    }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ ml: "auto", color: isDarkMode ? '#aaaaaa' : '#666666' }}>
                    {formatCreationTime(reply.creation_time)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        
        {/* Regular inbox display */}
        {inboxTab === "questions" ? (
          <List>
          {visibleMessages.length > 0 ? (
            visibleMessages.map((msg) => (
              <ListItem
                button
                key={msg.id}
                divider
                onClick={() => handleLocalMessageClick(msg)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  transition: "background-color 0.3s",
                  "&:hover": { backgroundColor: isDarkMode ? "#172836" : "rgba(0, 0, 0, 0.04)" },
                  borderBottom: `1px solid ${isDarkMode ? '#333333' : '#e0e0e0'}`,
                  py: 1.5
                }}
              >
                <Box sx={{ width: '100%' }}>
                <ListItemText
                  primary={msg.shortText}
                  primaryTypographyProps={{
                    fontWeight: !msg.is_read || clickedMessages.has(msg.id) ? "normal" : "bold",
                    color: isDarkMode ? '#ffffff' : '#000000'
                  }}
                />
                </Box>
                {msg.payment_amount > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#aaaaaa' : '#666666', mr: 0.5 }}>
                      {msg.payment_amount} ⭐
                    </Typography>
                  </Box>
                )}
              
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mr: 2, color: isDarkMode ? '#aaaaaa' : '#666666' }}>
                    {formatCreationTime(msg.creation_time)}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMessageClick(msg);
                    }}
                    sx={{ 
                      textTransform: "none", 
                      fontSize: "0.8rem",
                      bgcolor: "#5188c1",
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: '#3a6fa5'
                      }
                    }}
                  >
                    {getText("reply", language)}
                  </Button>
                </Box>
              </ListItem>
            ))
          ) : (
            <ListItem key="placeholder-question" divider sx={{ borderBottom: `1px solid ${isDarkMode ? '#333333' : '#e0e0e0'}` }}>
              <ListItemText
                primary={getText("noMessages", language)}
                secondary={getText("waitingForQuestions", language)}
                primaryTypographyProps={{
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
                secondaryTypographyProps={{
                  color: isDarkMode ? '#aaaaaa' : '#666666'
                }}
              />
            </ListItem>
          )}
        </List>
        ) : (
          <List>
            {visibleReplies.length > 0 && !isGroup ? (
              visibleReplies.map((reply) => (
                <ListItem
                  button
                  key={reply.id}
                  divider
                  onClick={() => handleLocalReplyClick(reply)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    transition: "background-color 0.3s",
                    "&:hover": { backgroundColor: isDarkMode ? "#172836" : "rgba(0, 0, 0, 0.04)" },
                    borderBottom: `1px solid ${isDarkMode ? '#333333' : '#e0e0e0'}`,
                    py: 1.5
                  }}
                >
                  <ListItemText 
                    primary={`${getText("questionDiv", language)} ${reply.message_text}`}
                    secondary={`${getText("replyDiv", language)} ${reply.fullText}`}
                    primaryTypographyProps={{
                      fontWeight: !reply.is_read || clickedReplies.has(reply.id) ? "normal" : "bold",
                      color: isDarkMode ? '#ffffff' : '#000000'
                    }}
                    secondaryTypographyProps={{
                      color: isDarkMode ? '#aaaaaa' : '#666666'
                    }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ ml: "auto", color: isDarkMode ? '#aaaaaa' : '#666666' }}>
                    {formatCreationTime(reply.creation_time)}
                  </Typography>
                </ListItem>
              ))
            ) : (
              <ListItem key="placeholder-reply" divider sx={{ borderBottom: `1px solid ${isDarkMode ? '#333333' : '#e0e0e0'}` }}>
                <ListItemText 
                  primary={getText("noMessages", language)} 
                  secondary={getText("waitingForQuestions", language)}
                  primaryTypographyProps={{
                    color: isDarkMode ? '#ffffff' : '#000000'
                  }}
                  secondaryTypographyProps={{
                    color: isDarkMode ? '#aaaaaa' : '#666666'
                  }}
                />
              </ListItem>
            )}
          </List>
        )}
      </Box>
      
      {/* Pagination */}
      {((inboxTab === "questions" && messages.length > 10) || (inboxTab === "answers" && replies.length > 10)) && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, pb: 2 }}>
          <Pagination 
            count={Math.ceil((inboxTab === "questions" ? messages.length : replies.length) / itemsPerPage)}
            page={inboxTab === "questions" ? currentPage : repliesPage}
            onChange={handlePaginationChange}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                color: isDarkMode ? '#ffffff' : '#000000'
              },
              '& .Mui-selected': {
                backgroundColor: isDarkMode ? '#172836' : '#e9f0f8',
                color: '#5188c1'
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default InboxPage;